import { useCallback, Fragment } from "react";
import { useStytchB2BClient, useStytchMember } from "@stytch/react/b2b";
import { useLocation, Link } from "react-router-dom";
import {
  Home as HomeIcon,
  Bell as BellIcon,
  Menu as Bars3Icon,
  Activity as ActivityIcon,
  Wheat as GrainIcon,
  FlaskConical as FlaskConicalIcon,
  LineChart as LineChartIcon,
  X as XMarkIcon,
  Upload as UploadIcon,
  BarChartBig as BarChartBigIcon,
  Zap as ZapIcon,
  ChevronDown,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";
import { Button } from "./button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@radix-ui/react-collapsible";
import { cn as classNames, cn } from "../../lib/utils";
import {
  PossibleRole,
  PossiblePackage,
  getUserRole,
  doesUserHavePackages,
} from "../../lib/models/auth";
import { RefreshSession } from "../../section/StytchRefreshSession";
import { pagesList } from "../../section/router";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "../ui/sidebar";
import NotificationsSection from "@/section/user_notifications/NotificationsSection";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useGetUnreadCount } from "@/lib/api/user_notification";
import { Badge } from "@/components/ui/badge";

type PageDefinition = ReturnType<typeof pagesList>[0];

function MainContent({ children }: { children: JSX.Element }) {
  const { state } = useSidebar();
  const sidebarWidth = state === "expanded" ? "16rem" : "2.5rem";
  
  return (
    <SidebarInset 
      className="px-4"
      style={{
        width: `calc(100% - (${sidebarWidth} + 10px))`,
      } as React.CSSProperties}
    >
      <div className="sticky top-0 z-30 mb-2 flex h-14 items-center gap-4 border-b bg-background px-4 sm:static sm:h-auto sm:border-0 sm:bg-transparent sm:px-6 py-4 lg:py-2 px-2 md:hidden">
        <SidebarTrigger />
      </div>
      <div className="mt-2">
        {children}
      </div>
    </SidebarInset>
  );
}

export default function AppPanel({ children }: { children: JSX.Element }) {
  const { member } = useStytchMember();
  const location = useLocation();
  const pathname = location.pathname;

  const navigation = pagesList({
    member,
    currentPathname: pathname,
    filterByPackages: true,
  }).filter((item) => !item.excludeFromDashboard);

  const currentPage = navigation.find((nav) => nav.current) || { name: "" };
  if (!member) return null;

  return (
    <SidebarProvider>
      <RefreshSession />
      <AppSidebar navigation={navigation} member={member} />
      <MainContent>{children}</MainContent>
    </SidebarProvider>
  );
}

function AppSidebar({
  navigation,
  member,
}: {
  navigation: ReturnType<typeof pagesList>;
  member: ReturnType<typeof useStytchMember>["member"];
}) {
  // Group navigation items
  const groupedNavigation = navigation.reduce<{ [key: string]: PageDefinition[]; }>(
    (acc, item) => {
      if (item.group) {
        if (!acc[item.group]) {
          acc[item.group] = [];
        }
        acc[item.group].push(item);
      } else {
        acc.general.push(item);
      }

      return acc;
    },
    { general: [] }
  );

  const groups = Object.entries(groupedNavigation).filter(([key]) => key !== 'general');
  const dashboardItem = navigation.find((nav) => nav.name === "Dashboard")!;

  return (
    <Sidebar
      side="left"
      variant="sidebar"
      collapsible="icon"
      className="border-r bg-red-600 text-white"
      style={{
        "--sidebar-width-icon": "2.5rem",
      } as React.CSSProperties}
    >
      <SidebarHeader className="p-2 flex items-center gap-2 border-b border-red-700 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:p-1 flex-row justify-between">
        <SidebarTrigger className="h-8 w-8 text-white hover:bg-red-700" />
        <NotificationDialog />
      </SidebarHeader>
      <SidebarContent
        className="text-slate-200 group-data-[collapsible=icon]:hidden"
      >
        <SidebarMenu className="mt-4">
          <SidebarMenuItem key={`sidebar-dashboard`} className="flex flex-row jutify-around">
            <SidebarMenuButton
              asChild
              isActive={dashboardItem?.current}
              tooltip={{
                children: "Dashboard",
                side: "right",
                align: "center",
              }}
              className={classNames(
                " hover:text-white",
                dashboardItem?.current
                  ? "text-white"
                  : "text-slate-200",
                "[&>svg]:text-slate-200 [&>svg]:group-hover:text-white",
                "px-2 mx-1",
                dashboardItem?.current && "[&>svg]:text-white",
              )}
            >
              <Link to={dashboardItem.href!} className="gap-x-3">
                <span>Dashboard</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {groups.map(([groupName, items]) => (
            <Collapsible key={groupName} defaultOpen={items.some(item => item.current)} className={cn("group/collapsible")}>
              <SidebarMenuItem className={cn("p-0 m-0 mb-1 bg-red-600")}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className={cn("flex row items-center justify-start w-full py-2 h-auto text-sm font-medium text-slate-200 bg-red-600 hover:bg-red-700/50 hover:text-white [&>svg.chevron]:ml-auto", items.some(item => item.current) ? "font-bold" : "font-normal")}>
                    <span className="">{groupName}</span>
                    <ChevronDown className="chevron h-4 w-4 ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180 text-slate-200 group-hover/collapsible:text-white" />
                  </Button>
                </CollapsibleTrigger>
              </SidebarMenuItem>
              <CollapsibleContent>
                <div className="pl-4 font-normal">
                  {items.map((item) => (
                    <SidebarMenuItem key={`${groupName}-${item.name}`}>
                      <SidebarMenuButton
                        asChild
                        isActive={item.current}
                        tooltip={{
                          children: item.name,
                          side: "right",
                          align: "center",
                        }}
                        className={classNames(
                          "hover:bg-red-700 hover:text-white",
                          item.current
                            ? "bg-red-700 text-white"
                            : "text-slate-200",
                          "[&>svg]:text-slate-200 [&>svg]:group-hover:text-white",
                          "px-2 mx-1",
                          item.current && "[&>svg]:text-white",
                        )}
                      >
                        <Link to={item.href} className="gap-x-3">
                          <span>{item.name}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
        </SidebarMenu>
      </SidebarContent>
    </Sidebar>
  );
}

// NOTE: not typed because stytch has wrong types
function hasProfile(member: any) {
  return member?.oauth_registrations?.length > 0;
}

function getProfilePicture(member: any) {
  return member.oauth_registrations[0]?.profile_picture_url || "";
}

function NotificationDialog() {
  const { data: unreadCountData } = useGetUnreadCount();
  const unreadCount = unreadCountData?.data?.data || 0;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <div className="relative inline-block cursor-pointer text-white hover:text-slate-100">
          <BellIcon className="h-6 w-6" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 bg-white text-red-600 border-0"
              variant="secondary"
            >
              <span className="text-xs font-bold">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            </Badge>
          )}
        </div>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] min-h-[90vh]  h-[90vh]  overflow-y-auto lg:max-w-5xl xl:max-w-6xl">
        <DialogTitle className="sr-only">Notifications</DialogTitle>
        <DialogDescription className="sr-only">Notifications Window</DialogDescription>
        <NotificationsSection />
      </DialogContent>
    </Dialog>
  );
}
